import React, { Component, type ComponentType } from 'react';
import '@atlaskit/css-reset';
import { ErrorBoundaryFlag as ErrorBoundary } from '@atlassian/jira-error-boundary-flag-renderer';
import { FireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useProjectAnalyticsAttributes } from '@atlassian/jira-software-report-charting-common/src/services/get-project/index.tsx';
import { SubProductUpdater } from '@atlassian/jira-spa-apps-common/src/analytics-sub-product/sub-product-updater/index.tsx';
import { AsyncNoSoftwareLicense } from '@atlassian/jira-spa-apps-common/src/error-screens/no-software-license/async';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';
import { LicenseCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment';
import { withRouter, type RouteContext } from '@atlassian/react-resource-router';
import { ProjectAppContainer } from './project-app-container';

type ReportPage = 'overview' | 'burnup' | 'velocity' | 'burndown' | 'cumulative';

type AppProps = {
	readonly boardId: number;
	readonly projectKey: string;
	readonly baseUrl: string;
};

type Props = RouteContext & {
	App: ComponentType<AppProps>;
};

// eslint-disable-next-line jira/react/no-class-components
export class Reports extends Component<Props> {
	getUrlParams() {
		const { match } = this.props;
		const { boardId, projectKey } = match.params;
		return {
			boardId: Number(boardId),
			projectKey,
		};
	}

	renderContents() {
		const { App } = this.props;
		const { boardId, projectKey } = this.getUrlParams();

		return (
			<TenantContextSubscriber>
				{({ tenantContext }) => (
					<ProjectAppContainer projectKey={projectKey || ''}>
						<SubProductUpdater subProduct="software" />
						<App baseUrl={tenantContext.baseUrl} projectKey={projectKey || ''} boardId={boardId} />
					</ProjectAppContainer>
				)}
			</TenantContextSubscriber>
		);
	}

	render() {
		return (
			<AuthenticationCheck>
				<LicenseCheck type="software">
					{({ result }) => (result ? this.renderContents() : <AsyncNoSoftwareLicense />)}
				</LicenseCheck>
			</AuthenticationCheck>
		);
	}
}

export const createReportPageBootstrap = (
	page: ReportPage,
	App: ComponentType<AppProps>, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<Record<any, any>> =>
	// @ts-expect-error - TS2322 - Type '{ (props: RouteContext & { history: BrowserHistory; } & { App: ComponentType<AppProps>; }): Element; displayName: string; }' is not assignable to type 'ComponentType<Record<any, any>>'.
	withRouter((props: Props) => {
		const projectInfo = useProjectAnalyticsAttributes();
		return (
			<UFOSegment name="report-page-bootstrap">
				<MarkProductStart />
				<ErrorBoundary id={`spa-app-bootstrap.next-gen.reports.${page}`}>
					<FireTrackAnalytics
						actionSubject="report"
						action="viewed"
						actionSubjectId={page}
						attributes={{ ...projectInfo }}
					/>
					<Reports {...props} App={App} />
				</ErrorBoundary>
			</UFOSegment>
		);
	});
